import { useLocale, useT } from '../../../i18n/locale-context';
import { useHydrated } from '../../../lib/use-hydrated';
import { LocaleLink } from '../../../i18n/locale-link';
import { tenderPath } from '../../../lib/slug';
import {
  formatBidSecurity,
  formatBudgetCompact,
  formatDeadline,
  daysLeft,
  daysWord,
} from '../../../lib/format';
import type { LandingLot } from '../../../lib/landing-client';

interface Props {
  lot: LandingLot;
  lockLabel: string;
  onClick: () => void;
  activeLabel: string;
  /**
   * Шаблон «осталось {{n}} {{word}}» (ключ `landing.lotCard.daysLeft`) — сам
   * счёт дней и согласованную форму слова (`daysWord`) компонент вычисляет
   * из `lot.deadline`, чтобы это не дублировалось в каждом месте вызова.
   */
  daysLeftTemplate: string;
  budgetLabel: string;
  bidSecurityLabel: string;
  deadlineLabel: string;
  contactLabel: string;
  docsLabel: string;
}

/**
 * Карточка живого лота. Ведёт на настоящую страницу тендера — в макете это
 * сделано намеренно, и это главный аргумент секции: «не примеры из рекламы,
 * а живой каталог». Бюджет/срок форматируются через `lib/format`, а не своим
 * кодом — узбекские числа и даты уже один раз ловили баг из-за отсутствия у
 * Chrome CLDR-данных для `uz` (см. комментарии в format.ts).
 */
export function LotCard({
  lot,
  lockLabel,
  onClick,
  activeLabel,
  daysLeftTemplate,
  budgetLabel,
  bidSecurityLabel,
  deadlineLabel,
  contactLabel,
  docsLabel,
}: Props) {
  const { locale } = useLocale();
  const t = useT();
  const compactBudget = formatBudgetCompact(
    lot.budgetUzs == null ? null : String(lot.budgetUzs),
    locale,
  );
  const deadlineText = formatDeadline(lot.deadline, locale);
  // Ноль обеспечения не бывает по факту данных — но `== null` пропускал бы
  // сохранённый 0 как «реальную сумму» и рисовал «Обеспечение 0 сум» вопреки
  // комментарию ниже. Гейт должен исключать и null, и 0 — это и делает
  // `formatBidSecurity`, возвращая null на обоих.
  //
  // Колонка двухединичная: часть источников публикует сумму в сумах, часть —
  // ПРОЦЕНТ от бюджета (0,2…3). Сырой вывод давал «Обеспечение 3 сум» на
  // миллиардном лоте; см. разбор в `lib/format.ts`.
  //
  // Опубликованная СУММА остаётся полной («22 072 644 сум») — лендинговая
  // карточка показывала её так с самого начала. Ставка печатается голой
  // («3 %») без вычисленной «≈ суммы»: это карточка, не страница лота
  // (решение Igor 11.09) — одно правило на поверхность, без проверок ширины.
  //
  // С 29.09.2026 API хранит такую ставку уже в сумах и присылает саму ставку
  // в `bidSecurityPct` — без неё карточка напечатала бы нашу арифметику
  // («276 084 661 сум») как опубликованную сумму.
  const depositText = formatBidSecurity(
    lot.bidSecurityUzs,
    lot.budgetUzs,
    locale,
    t('tender.budget.unit'),
    { publishedPct: lot.bidSecurityPct },
  );

  // Лоты этих секций всегда живые (см. докстринги NicheSwitcher/start.tsx) —
  // статус в API не приходит, поэтому пилюля не условная, а константная,
  // как и в макете. «Осталось N дней» — уже условно, по наличию дедлайна.
  // Client-only — the landing is prerendered; see useHydrated.
  const hydrated = useHydrated();
  const days = hydrated ? daysLeft(lot.deadline) : null;
  const daysLeftText =
    days == null
      ? null
      : daysLeftTemplate.replace('{{n}}', String(days)).replace('{{word}}', daysWord(days, locale));

  return (
    <LocaleLink
      to={tenderPath(lot.slug, lot.id)}
      onClick={onClick}
      className="block rounded-xl border border-edge bg-bone-surface p-4 no-underline shadow-m-sm transition hover:-translate-y-0.5 hover:border-brand hover:shadow-m-md"
    >
      <div className="flex items-center justify-between gap-2.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-status-active-bg px-2.5 py-1 text-[11px] font-bold text-status-active-text">
          <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-status-active-dot" />
          {activeLabel}
        </span>
        {daysLeftText && <span className="text-xs text-ink-soft">{daysLeftText}</span>}
      </div>

      <div className="mb-2 mt-2.5 text-[15.5px] font-bold leading-[1.35] text-ink">{lot.title}</div>

      {lot.categoryName && (
        <span className="mb-2.5 mr-1.5 inline-block rounded-full border border-edge bg-bone px-2.5 py-[3px] text-xs font-semibold text-ink-soft">
          {lot.categoryName}
        </span>
      )}
      {lot.regionName && (
        <span className="mb-2.5 mr-1.5 inline-block rounded-full border border-edge bg-bone px-2.5 py-[3px] text-xs font-semibold text-ink-soft">
          {lot.regionName}
        </span>
      )}

      <div className="flex items-baseline justify-between gap-2.5 border-t border-edge pt-[9px]">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-muted">
          {budgetLabel}
        </span>
        <span className="text-[15.5px] font-extrabold text-brand">
          {compactBudget ?? '—'}
          {compactBudget && (
            <small className="ml-1 text-xs font-semibold text-ink-soft">
              {t('tender.budget.unit')}
            </small>
          )}
        </span>
      </div>

      {/* Ноль обеспечения не бывает: это либо реальная сумма, либо её нет —
          строку не рисуем вовсе, а не показываем «0 сум» (см. бриф задачи). */}
      {depositText && (
        <div className="flex items-baseline justify-between gap-2.5 border-t border-edge pt-[9px]">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-muted">
            {bidSecurityLabel}
          </span>
          <span className="text-sm font-extrabold text-ink">{depositText}</span>
        </div>
      )}

      {deadlineText && (
        <div className="flex items-baseline justify-between gap-2.5 border-t border-edge pt-[9px]">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-muted">
            {deadlineLabel}
          </span>
          <span className="text-sm font-extrabold text-ink">{deadlineText}</span>
        </div>
      )}

      <div className="mt-0.5 flex items-center justify-between gap-2.5 border-t border-dashed border-edge pt-[9px]">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-muted">
          {contactLabel}
        </span>
        <span className="inline-block h-[11px] w-[110px] rounded-md bg-gradient-to-r from-[#C9C8C2] to-[#E4E3DE] blur-[3px]" />
        <span className="whitespace-nowrap rounded-md bg-brand-light px-2 py-[3px] text-xs font-bold text-brand">
          {lockLabel}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2.5 border-t border-dashed border-edge pt-[9px]">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-muted">
          {docsLabel}
        </span>
        <span className="inline-block h-[11px] w-[70px] rounded-md bg-gradient-to-r from-[#C9C8C2] to-[#E4E3DE] blur-[3px]" />
        <span className="whitespace-nowrap rounded-md bg-brand-light px-2 py-[3px] text-xs font-bold text-brand">
          {lockLabel}
        </span>
      </div>
    </LocaleLink>
  );
}
